import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';

/**
 * OIDC-клиент платформы мероприятий.
 *
 * Конфигурация повторяет lk-web / promtourism-web: один realm `inpdp`, поэтому
 * пользователь, уже вошедший в ЛК, попадает сюда без повторного ввода пароля
 * (см. §7 docs/event-platform-tz.md).
 *
 * Регистрация участников по ссылке-приглашению идёт через свою форму: человек
 * заполняет анкету, а учётка создаётся на последнем шаге. Токены при этом
 * приходят от бэкенда, а не из редиректа — их принимает adoptTokens().
 */
const userManager = new UserManager({
  authority: `${import.meta.env.VITE_KEYCLOAK_URL}/realms/${import.meta.env.VITE_KEYCLOAK_REALM}`,
  client_id: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
  redirect_uri: `${window.location.origin}/auth/callback`,
  post_logout_redirect_uri: window.location.origin,
  response_type: 'code',
  scope: 'openid profile email',
  loadUserInfo: true,
  automaticSilentRenew: true,
  silent_redirect_uri: `${window.location.origin}/auth/silent-renew.html`,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
});

export { userManager };
export type { User };

/**
 * Уход на Keycloak.
 *
 * `loginHint` подставляет адрес в форму входа. На шаге идентификации человек
 * его уже ввёл, и заставлять набирать второй раз — верный способ получить
 * опечатку и вход не в тот аккаунт.
 */
export function startLogin(returnTo?: string, loginHint?: string): Promise<void> {
  return userManager.signinRedirect({
    state: returnTo ? { returnTo } : undefined,
    login_hint: loginHint,
  });
}

export async function completeLogin(): Promise<{ user: User; returnTo: string }> {
  const user = await userManager.signinRedirectCallback();
  const state = user.state as { returnTo?: string } | undefined;
  return { user, returnTo: state?.returnTo ?? '/' };
}

export function startLogout(): Promise<void> {
  return userManager.signoutRedirect();
}

export function getStoredUser(): Promise<User | null> {
  return userManager.getUser();
}

/** Набор токенов ровно в том виде, в каком его отдаёт Keycloak. */
export interface RawTokens {
  access_token: string;
  refresh_token: string;
  id_token: string;
  token_type: string;
  expires_in: number;
  scope: string;
  session_state?: string;
}

/**
 * Принять токены, полученные не через редирект.
 *
 * Нужно ровно одному случаю: человек только что сам придумал пароль в конце
 * анкеты, бэкенд создал учётку и вошёл за него. Гнать его после этого на
 * форму входа — просить ввести пароль, который он набрал десять секунд назад.
 *
 * Сессия собирается вручную, потому что UserManager умеет заводить её только
 * из ответа на redirect. Профиль берём из id_token: подписи мы здесь не
 * проверяем — токен пришёл от нашего же бэкенда по HTTPS, а любой запрос с
 * ним всё равно будет проверен по JWKS на стороне API.
 */
export async function adoptTokens(tokens: RawTokens): Promise<User | null> {
  const profile = decodeJwtPayload(tokens.id_token);
  if (!profile?.sub) return null;

  const user = new User({
    access_token: tokens.access_token,
    refresh_token: tokens.refresh_token,
    id_token: tokens.id_token,
    token_type: tokens.token_type,
    scope: tokens.scope,
    session_state: tokens.session_state ?? null,
    expires_at: Math.floor(Date.now() / 1000) + tokens.expires_in,
    profile: profile as User['profile'],
  });

  await userManager.storeUser(user);
  // Положить в хранилище мало: таймер автопродления UserManager заводит на
  // событии загрузки, а не на записи. Без этой строки токен, полученный от
  // бэкенда, молча умирает через пять минут — и человек, заполнявший длинную
  // анкету, получает отказ авторизации на кнопке «Отправить».
  await userManager.events.load(user, false);
  return user;
}

/**
 * Свежий токен доступа.
 *
 * Возвращает текущий, пока до истечения остаётся больше запаса, и молча
 * обновляет его, когда времени в обрез. Запас в полминуты — на дорогу
 * запроса: токен, годный «ещё две секунды», сервер уже не примет.
 */
const REFRESH_MARGIN_SECONDS = 30;

export async function freshAccessToken(): Promise<string | null> {
  const user = await userManager.getUser();
  if (!user) return null;

  const expiresIn = user.expires_at ? user.expires_at - Math.floor(Date.now() / 1000) : 0;
  if (expiresIn > REFRESH_MARGIN_SECONDS) return user.access_token;

  try {
    const renewed = await userManager.signinSilent();
    return renewed?.access_token ?? null;
  } catch {
    // Обновиться не вышло — отдаём что есть: возможно, запрос публичный или
    // сервер ещё примет токен. Ошибку покажет сам ответ.
    return user.access_token;
  }
}

function decodeJwtPayload(jwt: string): Record<string, unknown> | null {
  try {
    const payload = jwt.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    // Токен приходит в UTF-8, а atob отдаёт байты по одному символу: без
    // перекодировки кириллица в имени превратится в мусор.
    const text = decodeURIComponent(
      Array.from(json, (c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''),
    );
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return null;
  }
}
